/**
 * Die Prüfung — Seitenleiste, Kopfzeile, Datenhaltung.
 *
 * Aufbau nach der Vorlage: links die Seitenleiste mit Übersicht,
 * Unterlagen, Rückfragen und Prüfprotokoll; rechts der Inhalt. Die fünf
 * Prüfschritte sind keine eigenen Menüpunkte, sondern Karten auf der
 * Übersicht — man geht hinein und wieder zurück.
 *
 * Der Zustand liegt bewusst hier oben und nicht in den einzelnen Seiten:
 * Ein Statuswechsel in der Belegprüfung muss sofort die Zähler in der
 * Seitenleiste und den Fortschrittsbalken ändern. Zwei Quellen für
 * dieselbe Zahl sind in einem Prüfwerkzeug schlimmer als ein etwas
 * größerer Zustandsbaum.
 *
 * Beide Rollen nutzen dieselbe Ansicht: Der Beirat kommt über seinen Link,
 * die Hausverwaltung über ihre Anmeldung. Was die eine darf und die andere
 * nicht, entscheidet die Rolle — und der Server noch einmal.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Belegpruefung } from './Belegpruefung';
import { Uebersicht } from './Uebersicht';
import { Bank } from './Bank';
import { Journal } from './Journal';
import { Protokoll } from './Protokoll';
import { SchrittSeite } from './beirat/SchrittSeite';
import { Salden } from './beirat/Salden';
import { UnterlagenListe } from './beirat/Unterlagen';
import { Rueckfragen } from './beirat/Rueckfragen';
import { Dokumentansicht } from '../komponenten/Dokumentansicht';
import { Fortschritt } from '../komponenten/Fortschritt';
import { Haken } from '../komponenten/Haken';
import { LeistenSchalter, useLeisteSchmal } from '../komponenten/Leiste';
import { AssistentFenster, AssistentKnopf, type Nachricht } from '../komponenten/Assistent';
import { api, ApiFehler, datum, eur, tageBis, zugangToken } from '../lib/api';
import { DEMO, demoRolle, demoRolleSetzen, demoZuruecksetzen } from '../demo/modus';
import type {
  Banktransaktion,
  Beleg,
  Dokument,
  Gruppe,
  Journal as JournalDaten,
  Kategorie,
  Kennzahlen,
  Kommentar,
  Kontoauszug,
  PdfRechteck,
  Pruefung,
  PruefStatus,
  Rolle,
  Schritt,
  SchrittStatus,
  Zahlungsbefund,
  Zuordnung,
} from '../lib/typen';

type Bereich = 'uebersicht' | 'schritt' | 'belege' | 'unterlagen' | 'rueckfragen' | 'protokoll';
type Werkbank = 'belege' | 'journal' | 'bank';

export interface PruefungsansichtProps {
  pruefungId: string;
  /** Zurück zur Verwaltungsseite — nur für angemeldete Benutzer. */
  onVerwaltung?: () => void;
}

export function Pruefungsansicht({ pruefungId, onVerwaltung }: PruefungsansichtProps) {
  const [pruefung, setPruefung] = useState<Pruefung | null>(null);
  const [rolle, setRolle] = useState<Rolle>('verwaltung');
  const [name, setName] = useState('');
  const [hausverwaltung, setHausverwaltung] = useState<string | null>(null);
  const [bereich, setBereich] = useState<Bereich>('uebersicht');
  const [schrittNr, setSchrittNr] = useState(1);
  const [werkbank, setWerkbank] = useState<Werkbank>('belege');
  const [unterlagenMenue, setUnterlagenMenue] = useState(false);
  const [schrittDrei, setSchrittDrei] = useState<'zu' | 'abschluss'>('zu');
  const [anmerkungDrei, setAnmerkungDrei] = useState('');

  const [schritte, setSchritte] = useState<Schritt[]>([]);
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [dokumente, setDokumente] = useState<Dokument[]>([]);
  const [offenesDokument, setOffenesDokument] = useState<string | null>(null);
  /** Eine markierte Stelle, die beim Öffnen der Unterlage gezeigt werden soll. */
  const [dokumentStelle, setDokumentStelle] = useState<PdfRechteck | null>(null);

  const [belege, setBelege] = useState<Beleg[]>([]);
  const [gliederung, setGliederung] = useState<Gruppe[]>([]);
  const [kennzahlen, setKennzahlen] = useState<Kennzahlen | null>(null);
  const [zahlungen, setZahlungen] = useState<Record<string, Zahlungsbefund>>({});
  const [belegeJePosition, setBelegeJePosition] = useState<Record<string, string[]>>({});
  const [ohneBeleg, setOhneBeleg] = useState<Set<string>>(new Set());
  const [zielPosition, setZielPosition] = useState<string | null>(null);
  const [transaktionStatus, setTransaktionStatus] = useState<Record<string, PruefStatus>>({});
  const [transaktionen, setTransaktionen] = useState<Banktransaktion[]>([]);
  const [journal, setJournal] = useState<JournalDaten | null>(null);
  const [zuordnungen, setZuordnungen] = useState<Zuordnung[]>([]);
  const [kommentare, setKommentare] = useState<Kommentar[]>([]);

  const [aktuellerBeleg, setAktuellerBeleg] = useState<string | null>(null);
  const [rueckfrage, setRueckfrage] = useState<{
    belegId?: string;
    transaktionId?: string;
    dokumentId?: string;
    schritt?: number;
    markierung?: PdfRechteck;
  } | null>(null);
  const [entwurf, setEntwurf] = useState('');
  const [hilfe, setHilfe] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [dunkel, setDunkel] = useState<boolean | null>(null);
  const [leisteOffen, setLeisteOffen] = useState(false);
  const [leisteSchmal, setLeisteSchmal] = useLeisteSchmal();
  const [assistentVerfuegbar, setAssistentVerfuegbar] = useState<boolean | null>(null);
  const [assistentOffen, setAssistentOffen] = useState(false);
  const [assistentVerlauf, setAssistentVerlauf] = useState<Nachricht[]>([]);

  // ── Laden ────────────────────────────────────────────────
  const alles = useCallback(async () => {
    try {
      const [kopf, belegDaten, zahlen, bank, zahlungsDaten, kommentarDaten, journalDaten, schrittDaten, dokDaten] =
        await Promise.all([
          api.pruefung(pruefungId),
          api.belege(pruefungId),
          api.kennzahlen(pruefungId),
          api
            .transaktionen(pruefungId)
            .catch(() => ({ transaktionen: [], zuordnungen: [], belegeJePosition: {}, ohneBeleg: [], status: {} }) as Kontoauszug),
          api.zahlungen(pruefungId).catch(() => ({}) as Record<string, Zahlungsbefund>),
          api.kommentare(pruefungId).catch(() => [] as Kommentar[]),
          api.journal(pruefungId).catch(() => ({ vorhanden: false }) as JournalDaten),
          api.schritte(pruefungId).catch(() => ({ schritte: [] as Schritt[], fortschritt: 0 })),
          api.dokumente(pruefungId).catch(() => ({ kategorien: [] as Kategorie[], dokumente: [] as Dokument[] })),
        ]);
      setPruefung(kopf.pruefung);
      setRolle(DEMO ? demoRolle() : kopf.rolle);
      setName((kopf as { name?: string }).name ?? '');
      setHausverwaltung((kopf as { hausverwaltung?: string | null }).hausverwaltung ?? null);
      setBelege(belegDaten.belege);
      setGliederung(belegDaten.gliederung);
      setKennzahlen(zahlen);
      setTransaktionen(bank.transaktionen);
      setZuordnungen(bank.zuordnungen);
      setBelegeJePosition(bank.belegeJePosition);
      setOhneBeleg(new Set(bank.ohneBeleg));
      setTransaktionStatus(Object.fromEntries(Object.entries(bank.status ?? {}).map(([id, z]) => [id, z.status])));
      setZahlungen(zahlungsDaten);
      setKommentare(kommentarDaten);
      setJournal(journalDaten);
      setSchritte(schrittDaten.schritte);
      setKategorien(dokDaten.kategorien);
      setDokumente(dokDaten.dokumente);
      setFehler(null);
    } catch (grund) {
      setFehler(
        grund instanceof ApiFehler && grund.status === 401
          ? 'Ihr Zugang ist abgelaufen oder ungültig. Bitte öffnen Sie den Link aus Ihrer Einladungsmail erneut — oder melden Sie sich als Hausverwaltung an.'
          : (grund as Error).message,
      );
    } finally {
      setLaedt(false);
    }
  }, [pruefungId]);

  useEffect(() => {
    void alles();
  }, [alles]);

  useEffect(() => {
    if (!meldung) return;
    const uhr = window.setTimeout(() => setMeldung(null), 4000);
    return () => window.clearTimeout(uhr);
  }, [meldung]);

  // ── Aktionen ─────────────────────────────────────────────

  const statusSetzen = useCallback(
    async (belegId: string, status: PruefStatus) => {
      const vorher = belege.find((b) => b.id === belegId)?.pruefstatus ?? 'offen';
      setBelege((alt) => alt.map((b) => (b.id === belegId ? { ...b, pruefstatus: status } : b)));
      setKennzahlen((alt) => (alt ? neuZaehlen(alt, vorher, status) : alt));
      try {
        await api.statusSetzen(belegId, pruefungId, status);
      } catch (grund) {
        setBelege((alt) => alt.map((b) => (b.id === belegId ? { ...b, pruefstatus: vorher } : b)));
        setKennzahlen((alt) => (alt ? neuZaehlen(alt, status, vorher) : alt));
        setMeldung(`Der Status konnte nicht gespeichert werden: ${(grund as Error).message}`);
      }
    },
    [pruefungId, belege],
  );

  const belegDrehen = useCallback(
    async (belegId: string, grad: 0 | 90 | 180 | 270) => {
      const vorher = belege.find((b) => b.id === belegId)?.drehung ?? 0;
      setBelege((alt) => alt.map((b) => (b.id === belegId ? { ...b, drehung: grad } : b)));
      try {
        await api.belegDrehen(belegId, pruefungId, grad);
      } catch (grund) {
        setBelege((alt) => alt.map((b) => (b.id === belegId ? { ...b, drehung: vorher } : b)));
        setMeldung(`Die Drehung konnte nicht gespeichert werden: ${(grund as Error).message}`);
      }
    },
    [pruefungId, belege],
  );

  useEffect(() => {
    if (!pruefungId) return;
    api.assistent
      .status(pruefungId)
      .then((a) => setAssistentVerfuegbar(a.verfuegbar))
      .catch(() => setAssistentVerfuegbar(false));
  }, [pruefungId]);

  const belegOeffnen = useCallback((belegId: string) => {
    setAktuellerBeleg(belegId);
    setOffenesDokument(null);
    setSchrittNr(3);
    setWerkbank('belege');
    setBereich('belege');
  }, []);

  const schrittOeffnen = useCallback((nummer: number, ansicht: Werkbank = 'belege') => {
    setSchrittNr(nummer);
    setOffenesDokument(null);
    // Schritt 3 ist die Belegansicht selbst — keine Zwischenseite.
    if (nummer === 3) {
      setWerkbank(ansicht);
      setBereich('belege');
    } else {
      setBereich('schritt');
    }
    window.scrollTo({ top: 0 });
  }, []);

  async function schrittStatus(nummer: number, status: SchrittStatus, anmerkung?: string) {
    try {
      const antwort = await api.schrittSetzen(pruefungId, nummer, status, anmerkung ?? null);
      setSchritte(antwort.schritte);
      setKennzahlen((alt) =>
        alt
          ? {
              ...alt,
              fortschritt: antwort.fortschritt,
              schritteErledigt: antwort.schritte.filter((s) => s.status === 'erledigt').length,
            }
          : alt,
      );
      if (status === 'erledigt') setMeldung(`Schritt ${nummer} abgeschlossen.`);
    } catch (grund) {
      setMeldung((grund as Error).message);
    }
  }

  async function dokumentStatus(dokumentId: string, status: PruefStatus) {
    const vorher = dokumente.find((d) => d.id === dokumentId)?.pruefstatus ?? 'offen';
    setDokumente((alt) =>
      alt.map((d) => (d.id === dokumentId ? { ...d, pruefstatus: status, pruefstatusVon: name, pruefstatusAm: new Date().toISOString() } : d)),
    );
    try {
      await api.dokumentStatus(pruefungId, dokumentId, status);
      setKennzahlen((alt) =>
        alt ? { ...alt, dokumenteGeprueft: alt.dokumenteGeprueft + (status === 'geprueft' ? 1 : 0) - (vorher === 'geprueft' ? 1 : 0) } : alt,
      );
    } catch (grund) {
      setDokumente((alt) => alt.map((d) => (d.id === dokumentId ? { ...d, pruefstatus: vorher } : d)));
      setMeldung((grund as Error).message);
    }
  }

  async function dokumentVermerk(dokumentId: string, vermerk: string) {
    const antwort = await api.dokumentStatus(pruefungId, dokumentId, undefined, vermerk);
    setDokumente((alt) => alt.map((d) => (d.id === dokumentId ? { ...d, vermerk: antwort.dokument?.vermerk ?? (vermerk.trim() || null) } : d)));
  }

  async function transaktionStatusSetzen(transaktionId: string, status: PruefStatus) {
    const vorher = transaktionStatus[transaktionId] ?? 'offen';
    setTransaktionStatus((alt) => ({ ...alt, [transaktionId]: status }));
    try {
      await api.transaktionStatus(transaktionId, pruefungId, status);
      setKennzahlen(await api.kennzahlen(pruefungId));
    } catch (grund) {
      setTransaktionStatus((alt) => ({ ...alt, [transaktionId]: vorher }));
      setMeldung((grund as Error).message);
    }
  }

  async function verknuepfen(transaktionId: string, belegId: string) {
    try {
      const ergebnis = await api.verknuepfen(pruefungId, transaktionId, belegId);
      await alles();
      setMeldung(`Verknüpft — ${eur(ergebnis.betrag)} dieser Zahlung entfallen auf den Beleg.`);
    } catch (grund) {
      setMeldung((grund as Error).message);
    }
  }

  async function zuordnungAendern(zuordnungId: string, was: 'bestaetigen' | 'loesen') {
    try {
      if (was === 'bestaetigen') await api.zuordnungBestaetigen(zuordnungId);
      else await api.zuordnungLoesen(zuordnungId);
      await alles();
      setMeldung(was === 'bestaetigen' ? 'Vorschlag bestätigt.' : 'Verknüpfung gelöst.');
    } catch (grund) {
      setMeldung((grund as Error).message);
    }
  }

  async function rueckfrageSenden() {
    if (!rueckfrage || !entwurf.trim()) return;
    try {
      await api.kommentarAnlegen(pruefungId, {
        belegId: rueckfrage.belegId,
        transaktionId: rueckfrage.transaktionId,
        dokumentId: rueckfrage.dokumentId,
        schritt: rueckfrage.schritt,
        text: entwurf.trim(),
        markierung: rueckfrage.markierung,
      });
      setRueckfrage(null);
      setEntwurf('');
      await alles();
      setMeldung('Rückfrage gestellt — die Hausverwaltung sieht sie unter Rückfragen.');
    } catch (grund) {
      setMeldung((grund as Error).message);
    }
  }

  async function belegeLaden() {
    setMeldung('Belege werden aus iDWELL geladen …');
    try {
      const ergebnis = await api.belegeLaden(pruefungId);
      await alles();
      setMeldung(`${ergebnis.geladen} Belege geladen.`);
    } catch (grund) {
      setMeldung((grund as Error).message);
    }
  }

  // ── Abgeleitetes ─────────────────────────────────────────
  const istBeirat = rolle === 'beirat';
  const abgeschlossen = pruefung?.status === 'abgeschlossen';
  const offeneRueckfragen = kommentare.filter((k) => !k.eltern_id && k.status === 'offen').length;
  const bankBefunde = (kennzahlen?.zahldatumAbweichungen ?? 0) + ohneBeleg.size;
  const restTage = tageBis(pruefung?.frist ?? null);
  const schritt = schritte.find((s) => s.nummer === schrittNr) ?? null;
  const katNach = useMemo(() => new Map(kategorien.map((k) => [k.id, k])), [kategorien]);
  const dokNach = useMemo(() => new Map(dokumente.map((d) => [d.id, d])), [dokumente]);
  const dokumentOffen = offenesDokument ? dokNach.get(offenesDokument) ?? null : null;

  /** Reihenfolge der Unterlagen, für Vor/Zurück im Dialog. */
  const dokReihe = useMemo(
    () =>
      [...dokumente].sort((a, b) => {
        const ka = katNach.get(a.kategorieId);
        const kb = katNach.get(b.kategorieId);
        return (ka?.schritt ?? 9) - (kb?.schritt ?? 9) || (ka?.reihenfolge ?? 0) - (kb?.reihenfolge ?? 0) || a.dateiname.localeCompare(b.dateiname);
      }),
    [dokumente, katNach],
  );

  const rueckfragenZumSchritt = (nr: number) =>
    kommentare.filter((k) => {
      if (k.eltern_id || k.status !== 'offen') return false;
      if (k.schritt) return k.schritt === nr;
      if (k.dokument_id) return katNach.get(dokNach.get(k.dokument_id)?.kategorieId ?? '')?.schritt === nr;
      if (k.beleg_id || k.transaktion_id) return nr === 3;
      return false;
    }).length;

  // ── Fehlerzustände ───────────────────────────────────────
  if (fehler) {
    return (
      <Rahmen>
        <div className="leer card">
          <h3>Kein Zugriff</h3>
          <p>{fehler}</p>
          {!zugangToken() && (
            <p className="note">
              In der Adresszeile fehlt der Zugangsparameter. Öffnen Sie den Link aus der E-Mail unverändert — oder{' '}
              <a href="/anmelden">melden Sie sich als Hausverwaltung an</a>.
            </p>
          )}
        </div>
      </Rahmen>
    );
  }

  const navigation: { wert: Bereich; text: string; zaehler?: number; warnend?: boolean }[] = [
    { wert: 'uebersicht', text: 'Übersicht' },
    { wert: 'unterlagen', text: 'Unterlagen', zaehler: dokumente.length || undefined },
    { wert: 'rueckfragen', text: 'Rückfragen', zaehler: offeneRueckfragen || undefined, warnend: offeneRueckfragen > 0 },
    { wert: 'protokoll', text: 'Prüfprotokoll' },
  ];
  const aktivImMenue: Bereich = bereich === 'schritt' || bereich === 'belege' ? 'uebersicht' : bereich;
  const brotkrumen =
    bereich === 'schritt' && schritt
      ? `Schritt ${schritt.nummer} · ${schritt.titel}`
      : bereich === 'belege'
        ? 'Schritt 3 · Kontenblätter und Belege'
        : navigation.find((n) => n.wert === bereich)?.text ?? '';

  return (
    <div className={`shell${leisteOffen ? ' leiste-offen' : ''}${leisteSchmal ? ' leiste-schmal' : ''}`}>
      <aside className="seitenleiste">
        <div className="marke">
          <div className="brandmark">iD</div>
          <div className="marke-text">
            <div className="marke-titel">Beiratsprüfung</div>
            <div className="marke-unter">Digitale Belegprüfung</div>
          </div>
        </div>
        <nav className="seitennav" aria-label="Bereiche">
          <div className="seitennav-label">Prüfungsbereich</div>
          {navigation.map((n) => (
            <button
              key={n.wert}
              className="seitennav-punkt"
              aria-current={aktivImMenue === n.wert ? 'page' : undefined}
              title={leisteSchmal ? `${n.text}${n.zaehler !== undefined ? ` (${n.zaehler})` : ''}` : undefined}
              onClick={() => {
                setBereich(n.wert);
                setLeisteOffen(false);
                setOffenesDokument(null);
              }}
            >
              <NavSymbol wert={n.wert} />
              <span>{n.text}</span>
              {n.zaehler !== undefined && <span className={`badge${n.warnend ? ' w' : ''}`}>{n.zaehler}</span>}
            </button>
          ))}
        </nav>
        {kennzahlen && schritte.length > 0 && (
          <div className="seitenleiste-fortschritt">
            <Fortschritt anteil={kennzahlen.fortschritt} text={`${kennzahlen.schritteErledigt} von ${kennzahlen.schritteGesamt}`} />
          </div>
        )}
        <div className="spacer" />
        <div className="seitenleiste-hilfe">
          <b>Hilfe & Kontakt</b>
          <p>
            Fehlt eine Unterlage oder ist etwas unklar? Stellen Sie eine Rückfrage — {hausverwaltung ?? 'die Hausverwaltung'} kann
            Dokumente nachreichen und antwortet direkt hier.
          </p>
          <button className="btn ghost sm" onClick={() => setHilfe(true)}>
            Tastaturhilfe
          </button>
        </div>
        <LeistenSchalter schmal={leisteSchmal} onWechsel={setLeisteSchmal} />
      </aside>

      <div className="hauptbereich">
        <header className="kopfzeile">
          <button className="iconbtn nur-schmal" aria-label="Menü" onClick={() => setLeisteOffen((o) => !o)}>
            ☰
          </button>
          <div className="brotkrumen" title={pruefung?.liegenschaft_bez}>
            <span className="brotkrumen-weg">Prüfungsbereich</span>
            <span className="brotkrumen-trenner">/</span>
            <span className="brotkrumen-ort">
              {pruefung?.liegenschaft_bez ?? '…'} · {pruefung?.bezeichnung ?? ''}
            </span>
            {brotkrumen && bereich !== 'uebersicht' && (
              <>
                <span className="brotkrumen-trenner">/</span>
                <span className="brotkrumen-ort">{brotkrumen}</span>
              </>
            )}
          </div>
          <div className="spacer" />
          {abgeschlossen ? (
            <span className="pill ok"><span className="dot" />Abgeschlossen</span>
          ) : pruefung?.status === 'freigegeben' ? (
            <span className="pill n"><span className="dot" />Prüfung freigegeben</span>
          ) : null}
          {restTage !== null && pruefung?.status === 'freigegeben' && (
            <div className={`frist${restTage <= 7 ? ' knapp' : ''}`}>
              noch <b>{Math.max(0, restTage)}</b> Tage
            </div>
          )}
          {onVerwaltung && (
            <button className="btn ghost sm" onClick={onVerwaltung}>
              ← Verwaltung
            </button>
          )}
          <div className="nutzer" title={name}>
            <span className="nutzer-kreis">{initialen(name || (istBeirat ? 'Beirat' : 'Hausverwaltung'))}</span>
            <span className="nutzer-text">
              <b>{name || (istBeirat ? 'Beirat' : 'Hausverwaltung')}</b>
              <small>
                {istBeirat ? 'Beirat' : 'Hausverwaltung'} · {pruefung?.liegenschaft_bez ?? ''}
              </small>
            </span>
          </div>
          <button
            className="iconbtn"
            title="Hell / Dunkel"
            aria-label="Darstellung wechseln"
            onClick={() => {
              const neu = !(dunkel ?? matchMedia('(prefers-color-scheme: dark)').matches);
              setDunkel(neu);
              document.documentElement.setAttribute('data-theme', neu ? 'dark' : 'light');
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
            </svg>
          </button>
        </header>

        {DEMO && (
          <div className="demoband">
            <b>Vorführfassung</b>
            <span>Echte Struktur, erfundene Beträge. Änderungen gelten nur bis zum Neuladen.</span>
            <div className="spacer" />
            <div className="seg" role="group" aria-label="Rolle">
              <button aria-pressed={rolle === 'verwaltung'} onClick={() => { demoRolleSetzen('verwaltung'); void alles(); }}>
                Hausverwaltung
              </button>
              <button aria-pressed={rolle === 'beirat'} onClick={() => { demoRolleSetzen('beirat'); void alles(); }}>
                Beirat
              </button>
            </div>
            <button className="btn ghost sm" onClick={() => { demoZuruecksetzen(); setAktuellerBeleg(null); setBereich('uebersicht'); void alles(); }}>
              Zurücksetzen
            </button>
          </div>
        )}

        {!laedt && belege.length === 0 && !istBeirat && pruefung?.belegquelle !== 'import' && (
          <div className="warnband">
            <span>Für diese Prüfung sind noch keine Belege aus iDWELL geladen.</span>
            <button className="btn sm" style={{ marginLeft: 'auto' }} onClick={belegeLaden}>
              Belege jetzt laden
            </button>
          </div>
        )}

        <main className="inhalt">
          <section className={`seite${bereich === 'belege' ? ' randlos' : ''}`}>
            {bereich === 'uebersicht' && (
              <Uebersicht
                pruefung={pruefung}
                name={name}
                hausverwaltung={hausverwaltung}
                kennzahlen={kennzahlen}
                schritte={schritte}
                kategorien={kategorien}
                dokumente={dokumente}
                kommentare={kommentare}
                istBeirat={istBeirat}
                onSchritt={schrittOeffnen}
                onUnterlagen={() => setBereich('unterlagen')}
                onRueckfragen={() => setBereich('rueckfragen')}
                onProtokoll={() => setBereich('protokoll')}
              />
            )}

            {bereich === 'schritt' && schritt && (
              <SchrittSeite
                pruefungId={pruefungId}
                schritt={schritt}
                schritte={schritte}
                kategorien={kategorien}
                dokumente={dokumente}
                rolle={rolle}
                abgeschlossen={Boolean(abgeschlossen)}
                offeneRueckfragen={rueckfragenZumSchritt(schritt.nummer)}
                onDokument={setOffenesDokument}
                onStatus={schrittStatus}
                onRueckfrage={(nr) => { setRueckfrage({ schritt: nr }); setEntwurf(''); }}
                onWechsel={schrittOeffnen}
                onZurueck={() => setBereich('uebersicht')}
              >
                {schritt.werkzeug === 'salden' && (
                  <Salden pruefungId={pruefungId} rolle={rolle} abgeschlossen={Boolean(abgeschlossen)} onMeldung={setMeldung} />
                )}
              </SchrittSeite>
            )}

            {bereich === 'belege' && (
              <div className="belege-rahmen">
                <div className="belege-leiste">
                  <button className="btn ghost sm" onClick={() => setBereich('uebersicht')}>
                    ← Übersicht
                  </button>
                  <div className="belege-titel">
                    <div className={`schritt-nummer ${schritte[2]?.status ?? 'offen'}`} aria-hidden="true">
                      {schritte[2]?.status === 'erledigt' ? '✓' : 3}
                    </div>
                    <div>
                      <b>Kontenblätter und Belege</b>
                      <small>
                        {kennzahlen?.belegeGeprueft ?? 0} von {kennzahlen?.belegeGesamt ?? belege.length} Belegen geprüft
                        {kennzahlen && kennzahlen.belegeRueckfrage > 0 ? ` · ${kennzahlen.belegeRueckfrage} mit Rückfrage` : ''}
                      </small>
                    </div>
                  </div>
                  <div className="seg" role="tablist" aria-label="Ansicht">
                    <button role="tab" aria-selected={werkbank === 'belege'} onClick={() => setWerkbank('belege')}>
                      Belege
                    </button>
                    <button role="tab" aria-selected={werkbank === 'bank'} onClick={() => setWerkbank('bank')}>
                      Kontoauszug
                      {bankBefunde > 0 && <span className="badge w">{bankBefunde}</span>}
                    </button>
                    <button role="tab" aria-selected={werkbank === 'journal'} onClick={() => setWerkbank('journal')}>
                      Journal
                      {kennzahlen?.journalVorhanden && kennzahlen.journalBefunde > 0 && <span className="badge w">{kennzahlen.journalBefunde}</span>}
                    </button>
                  </div>
                  <div className="spacer" />
                  <div className="menue-anker">
                    <button className="btn sm" aria-haspopup="menu" aria-expanded={unterlagenMenue} onClick={() => setUnterlagenMenue((o) => !o)}>
                      Unterlage einblenden ▾
                    </button>
                    {unterlagenMenue && (
                      <div className="menue" role="menu" onMouseLeave={() => setUnterlagenMenue(false)}>
                        {dokReihe.length === 0 && <div className="menue-leer">Noch keine Unterlagen bereitgestellt.</div>}
                        {[3, 1, 2, 4, 5].map((nr) => {
                          const doks = dokReihe.filter((d) => katNach.get(d.kategorieId)?.schritt === nr);
                          if (!doks.length) return null;
                          return (
                            <div key={nr} className="menue-gruppe">
                              <div className="menue-kopf">
                                Schritt {nr} · {schritte.find((x) => x.nummer === nr)?.titel}
                              </div>
                              {doks.map((d) => (
                                <button key={d.id} role="menuitem" onClick={() => { setUnterlagenMenue(false); setOffenesDokument(d.id); }}>
                                  <Haken zustand={d.pruefstatus} />
                                  <span className="menue-text">
                                    <b>{katNach.get(d.kategorieId)?.bezeichnung}</b>
                                    <small>{d.dateiname}</small>
                                  </span>
                                </button>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {!abgeschlossen && schritte[2]?.status !== 'erledigt' && (
                    <button className="btn primary sm" onClick={() => setSchrittDrei('abschluss')}>
                      ✓ Schritt abschließen
                    </button>
                  )}
                  {!abgeschlossen && schritte[2]?.status === 'erledigt' && (
                    <span className="pill ok"><span className="dot" />Abgeschlossen</span>
                  )}
                </div>

                {werkbank === 'belege' && belege.length > 0 && (
                  <Belegpruefung
                    pruefungId={pruefungId}
                    belege={belege}
                    gliederung={gliederung}
                    aktuellerBeleg={aktuellerBeleg ?? belege[0]?.id ?? null}
                    onBelegWechsel={setAktuellerBeleg}
                    onStatus={statusSetzen}
                    onRueckfrage={(belegId, markierung) => {
                      setRueckfrage({ belegId, markierung });
                      setEntwurf(markierung ? 'Zur markierten Stelle: ' : '');
                    }}
                    onHilfe={() => setHilfe(true)}
                    vorfilter={null}
                    zahlungen={zahlungen}
                    transaktionen={transaktionen}
                    onPosition={(transaktionId) => {
                      setZielPosition(transaktionId);
                      setWerkbank('bank');
                    }}
                    onDokumentStelle={(dokumentId, stelle) => {
                      setDokumentStelle(stelle);
                      setOffenesDokument(dokumentId);
                    }}
                    onFundstelle={(belegId) => api.fundstelle(pruefungId, belegId)}
                    onKiSuche={
                      assistentVerfuegbar
                        ? async (belegId) => {
                            const a = await api.belegKiAbgleich(pruefungId, belegId);
                            if (a.gefunden) {
                              await alles();
                              setMeldung(a.zugeordnet ? 'Die KI hat die Zahlung im Auszug gefunden und zugeordnet.' : 'Die KI hat eine Zahlung im Auszug vorgeschlagen.');
                            } else {
                              setMeldung(a.hinweise[0] ?? 'Die KI fand im Auszug keine passende Zahlung.');
                            }
                            return a.gefunden;
                          }
                        : undefined
                    }
                    onDrehen={belegDrehen}
                  />
                )}
                {werkbank === 'belege' && belege.length === 0 && (
                  <div className="wrap" style={{ paddingTop: 24 }}>
                    <div className="leer card">
                      <h3>Noch keine Belege</h3>
                      <p>
                        {istBeirat
                          ? `${hausverwaltung ?? 'Die Hausverwaltung'} hat für diese Prüfung noch keine Belege bereitgestellt. Die Kontenblätter und der Kontoauszug sind — soweit vorhanden — unter „Unterlage einblenden" zu finden.`
                          : pruefung?.belegquelle === 'import'
                            ? 'Legen Sie das Importpaket (Buchungsliste mit Belegordner) im Eingang der Verwaltung ab — die Belege erscheinen dann hier, nach Aufwandskonto sortiert.'
                            : 'Laden Sie die Belege aus iDWELL — oben im gelben Band.'}
                      </p>
                    </div>
                  </div>
                )}
                {werkbank === 'journal' && (
                  <div className="wrap belege-unterseite">
                    <Journal pruefungId={pruefungId} journal={journal} belege={belege} darfHochladen={!istBeirat} onNeuLaden={alles} onBeleg={belegOeffnen} onMeldung={setMeldung} />
                  </div>
                )}
                {werkbank === 'bank' && (
                  <div className="wrap belege-unterseite">
                    <Bank
                      pruefungId={pruefungId}
                      transaktionen={transaktionen}
                      zuordnungen={zuordnungen}
                      belegeJePosition={belegeJePosition}
                      ohneBeleg={ohneBeleg}
                      belege={belege}
                      darfHochladen={!istBeirat}
                      zielPosition={zielPosition}
                      onZielErledigt={() => setZielPosition(null)}
                      zahlungen={zahlungen}
                      transaktionStatus={transaktionStatus}
                      onTransaktionStatus={transaktionStatusSetzen}
                      onVerknuepfen={verknuepfen}
                      onZuordnung={zuordnungAendern}
                      onNeuLaden={alles}
                      onBeleg={belegOeffnen}
                      onRueckfrage={(transaktionId) => { setRueckfrage({ transaktionId }); setEntwurf(''); }}
                      onMeldung={setMeldung}
                      journalVorhanden={Boolean(journal?.vorhanden)}
                      onJournal={() => setWerkbank('journal')}
                    />
                  </div>
                )}
              </div>
            )}

            {bereich === 'unterlagen' && (
              <div className="wrap">
                <div className="seitenkopf">
                  <div>
                    <h2 style={{ margin: 0 }}>Unterlagen prüfen</h2>
                    <p className="s" style={{ margin: '4px 0 0' }}>
                      Alle Dokumente, die {hausverwaltung ?? 'die Hausverwaltung'} bereitgestellt hat, nach Prüfbereich. Klicken Sie eine Zeile, um sie zu öffnen.
                    </p>
                  </div>
                  <div className="spacer" />
                  <span className="chip">{istBeirat ? 'Lesemodus · Beirat' : 'Hausverwaltung'}</span>
                </div>
                <UnterlagenListe kategorien={kategorien} dokumente={dokumente} schritte={schritte} onOeffnen={setOffenesDokument} />
              </div>
            )}

            {bereich === 'rueckfragen' && (
              <Rueckfragen
                pruefungId={pruefungId}
                kommentare={kommentare}
                belege={belege}
                transaktionen={transaktionen}
                dokumente={dokumente}
                kategorien={kategorien}
                schritte={schritte}
                rolle={rolle}
                abgeschlossen={Boolean(abgeschlossen)}
                onBeleg={belegOeffnen}
                onDokument={(dokumentId, stelle) => {
                  setDokumentStelle(stelle ?? null);
                  setOffenesDokument(dokumentId);
                }}
                onSchritt={schrittOeffnen}
                onPosition={(transaktionId) => { setZielPosition(transaktionId); schrittOeffnen(3, 'bank'); }}
                onNeuLaden={alles}
                onMeldung={setMeldung}
              />
            )}

            {bereich === 'protokoll' && (
              <Protokoll
                pruefungId={pruefungId}
                pruefung={pruefung}
                kommentare={kommentare}
                kennzahlen={kennzahlen}
                schritte={schritte}
                rolle={rolle}
                name={name}
                onSchritt={schrittOeffnen}
                onNeuLaden={alles}
                onMeldung={setMeldung}
              />
            )}
          </section>
        </main>
      </div>

      {schrittDrei === 'abschluss' && (
        <div className="ovl" onMouseDown={(e) => e.target === e.currentTarget && setSchrittDrei('zu')}>
          <div className="sheet">
            <h3>Schritt 3 abschließen</h3>
            <p className="s">
              {kennzahlen?.belegeGeprueft ?? 0} von {kennzahlen?.belegeGesamt ?? belege.length} Belegen geprüft
              {kennzahlen && kennzahlen.belegeRueckfrage > 0 ? `, ${kennzahlen.belegeRueckfrage} mit offener Rückfrage` : ''}. Eine Stichprobe genügt — der Schritt gilt als abgeschlossen, wenn Sie ihn abschließen.
            </p>
            <label className="feld">
              <span>Anmerkung (optional, erscheint im Protokoll)</span>
              <textarea value={anmerkungDrei} onChange={(e) => setAnmerkungDrei(e.target.value)} rows={3} placeholder="z. B. Instandhaltung 5010 vollständig geprüft, übrige Konten stichprobenartig." autoFocus />
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button className="btn ghost" onClick={() => setSchrittDrei('zu')}>
                Abbrechen
              </button>
              <button className="btn" onClick={() => { setSchrittDrei('zu'); setRueckfrage({ schritt: 3 }); setEntwurf(''); }}>
                ? Rückfrage zum Schritt
              </button>
              <button
                className="btn primary"
                onClick={async () => {
                  await schrittStatus(3, 'erledigt', anmerkungDrei.trim() || undefined);
                  setSchrittDrei('zu');
                }}
              >
                ✓ Schritt abschließen
              </button>
            </div>
          </div>
        </div>
      )}

      {dokumentOffen && (
        <Dokumentansicht
          pruefungId={pruefungId}
          dokument={dokumentOffen}
          kategorie={katNach.get(dokumentOffen.kategorieId)}
          rolle={rolle}
          abgeschlossen={Boolean(abgeschlossen)}
          kommentare={kommentare}
          assistent={assistentVerfuegbar}
          stelle={dokumentStelle}
          onStatus={dokumentStatus}
          onVermerk={dokumentVermerk}
          onRueckfrage={(dokumentId, markierung) => {
            setRueckfrage({ dokumentId, markierung });
            setEntwurf(markierung ? 'Zur markierten Stelle: ' : '');
          }}
          onSchliessen={() => {
            setOffenesDokument(null);
            setDokumentStelle(null);
          }}
          onZurueck={dokReihe.indexOf(dokumentOffen) > 0 ? () => setOffenesDokument(dokReihe[dokReihe.indexOf(dokumentOffen) - 1].id) : undefined}
          onWeiter={dokReihe.indexOf(dokumentOffen) < dokReihe.length - 1 ? () => setOffenesDokument(dokReihe[dokReihe.indexOf(dokumentOffen) + 1].id) : undefined}
        />
      )}

      {!fehler && (assistentVerfuegbar !== false || !istBeirat) && (
        <AssistentKnopf offen={assistentOffen} onClick={() => setAssistentOffen((o) => !o)} />
      )}
      {assistentOffen && (
        <AssistentFenster
          pruefungId={pruefungId}
          bezug={{ dokumentId: dokumentOffen?.id ?? null, belegId: bereich === 'belege' && werkbank === 'belege' ? (aktuellerBeleg ?? belege[0]?.id ?? null) : null }}
          bezugText={
            dokumentOffen
              ? `${katNach.get(dokumentOffen.kategorieId)?.bezeichnung ?? 'Unterlage'} · ${dokumentOffen.dateiname}`
              : bereich === 'belege' && werkbank === 'belege' && (aktuellerBeleg ?? belege[0])
                ? `Beleg ${belege.find((b) => b.id === (aktuellerBeleg ?? belege[0]?.id))?.lieferant ?? ''} — und die ganze Prüfung`
                : null
          }
          verlauf={assistentVerlauf}
          onVerlauf={setAssistentVerlauf}
          verfuegbar={assistentVerfuegbar}
          istBeirat={istBeirat}
          onSchliessen={() => setAssistentOffen(false)}
        />
      )}

      {rueckfrage && (
        <div className="ovl" onMouseDown={(e) => e.target === e.currentTarget && setRueckfrage(null)}>
          <div className="sheet">
            {rueckfrage.belegId ? (
              <>
                <h3>Rückfrage zu {belege.find((b) => b.id === rueckfrage.belegId)?.lieferant}</h3>
                <p className="s mono" style={{ fontSize: 12 }}>
                  {belege.find((b) => b.id === rueckfrage.belegId)?.rechnungsnummer} · {datum(belege.find((b) => b.id === rueckfrage.belegId)?.rechnungsdatum)}
                </p>
              </>
            ) : rueckfrage.transaktionId ? (
              <>
                <h3>Rückfrage zur Abbuchung {transaktionen.find((t) => t.id === rueckfrage.transaktionId)?.partnerName ?? ''}</h3>
                <p className="s mono" style={{ fontSize: 12 }}>
                  {datum(transaktionen.find((t) => t.id === rueckfrage.transaktionId)?.buchungsdatum)} ·{' '}
                  {transaktionen.find((t) => t.id === rueckfrage.transaktionId)?.verwendungszweck ?? 'ohne Verwendungszweck'}
                </p>
              </>
            ) : rueckfrage.dokumentId ? (
              <>
                <h3>Rückfrage zu {katNach.get(dokNach.get(rueckfrage.dokumentId)?.kategorieId ?? '')?.bezeichnung ?? 'Unterlage'}</h3>
                <p className="s" style={{ fontSize: 12 }}>{dokNach.get(rueckfrage.dokumentId)?.dateiname}</p>
              </>
            ) : (
              <>
                <h3>Rückfrage zu Schritt {rueckfrage.schritt}</h3>
                <p className="s" style={{ fontSize: 12 }}>{schritte.find((s) => s.nummer === rueckfrage.schritt)?.titel}</p>
              </>
            )}
            <textarea value={entwurf} onChange={(e) => setEntwurf(e.target.value)} placeholder="Was soll die Hausverwaltung klären?" autoFocus />
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button className="btn ghost" onClick={() => setRueckfrage(null)}>
                Abbrechen
              </button>
              <button className="btn primary" onClick={rueckfrageSenden} disabled={!entwurf.trim()}>
                Rückfrage senden
              </button>
            </div>
          </div>
        </div>
      )}

      {hilfe && (
        <div className="ovl" onMouseDown={(e) => e.target === e.currentTarget && setHilfe(false)}>
          <div className="sheet">
            <h3>Tastaturbedienung</h3>
            <p className="s">In der Belegprüfung. Wer 187 Belege durchsieht, klickt nicht.</p>
            <div className="kbdgrid">
              <kbd>J</kbd><span>Nächster Beleg</span>
              <kbd>K</kbd><span>Vorheriger Beleg</span>
              <kbd>G</kbd><span>Als geprüft markieren und weiter</span>
              <kbd>R</kbd><span>Rückfrage öffnen</span>
              <kbd>F</kbd><span>Fokus ins Suchfeld</span>
              <kbd>[</kbd><span>Linke Spalte ein- und ausblenden</span>
              <kbd>+ / −</kbd><span>Zoom im PDF</span>
              <kbd>Esc</kbd><span>Dialog schließen</span>
              <kbd>?</kbd><span>Diese Hilfe</span>
            </div>
            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <button className="btn" onClick={() => setHilfe(false)}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {meldung && <div className="toast">{meldung}</div>}
    </div>
  );
}

export function Rahmen({ children }: { children: React.ReactNode }) {
  return (
    <div className="app">
      <header className="appbar">
        <div className="appbar-row">
          <div className="brandmark">iD</div>
          <div className="prop">Beiratsprüfung</div>
        </div>
      </header>
      <main className="inhalt">
        <section className="seite">
          <div className="wrap">{children}</div>
        </section>
      </main>
    </div>
  );
}

function NavSymbol({ wert }: { wert: Bereich }) {
  const gemeinsam = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (wert) {
    case 'uebersicht':
      return <svg {...gemeinsam}><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></svg>;
    case 'unterlagen':
      return <svg {...gemeinsam}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /></svg>;
    case 'rueckfragen':
      return <svg {...gemeinsam}><path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z" /></svg>;
    default:
      return <svg {...gemeinsam}><path d="M9 3h6l1 2h3v16H5V5h3z" /><path d="M9 12l2 2 4-4" /></svg>;
  }
}

function initialen(name: string): string {
  const teile = name.trim().split(/\s+/).filter(Boolean);
  if (!teile.length) return '·';
  return (teile[0][0] + (teile.length > 1 ? teile[teile.length - 1][0] : '')).toUpperCase();
}

/** Zählt die Kennzahlen bei einem Statuswechsel fort, ohne neu zu laden. */
function neuZaehlen(alt: Kennzahlen, vorher: PruefStatus, nachher: PruefStatus): Kennzahlen {
  if (vorher === nachher) return alt;
  const feld = { offen: 'belegeOffen', geprueft: 'belegeGeprueft', rueckfrage: 'belegeRueckfrage' } as const;
  return {
    ...alt,
    [feld[vorher]]: Math.max(0, alt[feld[vorher]] - 1),
    [feld[nachher]]: alt[feld[nachher]] + 1,
  };
}
